import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getRailwayUrl } from '@lib/railwayUrl';
import { routes } from '@lib/routes';

type VerificationStatus = 'submitted' | 'processing' | 'completed' | 'failed';
type Signal = 'high' | 'medium' | 'low' | 'none' | 'error';
type Rating = 'A' | 'B' | 'C' | 'D' | 'E';

interface EvidenceSection {
  label: string;
  value: string;
}

interface EvidenceItem {
  tool: string;
  label: string;
  signal: Signal;
  detail: string;
  imageUrl?: string;
  rawValue?: number | null;
  sections?: EvidenceSection[];
}

interface EvidenceJson {
  verificationId: string;
  rating: Rating;
  ratingLabel: string;
  ratingReason: string;
  evidence: EvidenceItem[];
  fileInfo: {
    fileName: string;
    fileHash: string;
    mimeType: string;
    analysisTimestamp: string;
    rfc3161TokenUrl?: string;
  };
}

interface VerificationResponse {
  verificationId: string;
  status: VerificationStatus;
  evidenceJson: string | EvidenceJson | null;
  reportUrl: string | null;
  fileName: string;
  fileType: string;
  fileHash: string;
  createdAt?: string;
  custodyEvents?: StreamEvent[];
}

interface StoredFileInfo {
  name: string;
  size: number;
  type: string;
  selectedType: 'image';
  preview?: string | null;
}

interface StreamEvent {
  id: string;
  type: string;
  createdAt?: string;
  metadata?: Record<string, unknown> | null;
}

interface TraceLine {
  id: string;
  label: string;
  tone: 'default' | 'error' | 'working';
}

const EVENT_LABELS: Record<string, string> = {
  upload_received: '↑  File uploaded and stored',
  download_complete: '↓  File downloaded and hash verified',
  ela_complete: '✓  Error Level Analysis',
  external_apis_complete: '✓  External detection APIs (Hive · Sightengine · Reality Defender)',
  metadata_complete: '✓  Metadata forensics',
  jpeg_compression_complete: '✓  Compression history analysis',
  gemini_vision_complete: '✓  VLM visual forensics',
  merge_complete: '⚙  Merging results...',
  synthesis_complete: '✓  ABCDE rating computed',
  analysis_complete: '✓  Analysis complete',
  analysis_started: '↻  Analysis started',
};

const SIGNAL_CLASSES: Record<Signal, string> = {
  high: 'border-red-200 bg-red-50 text-red-700',
  medium: 'border-amber-200 bg-amber-50 text-amber-700',
  low: 'border-green-200 bg-green-50 text-green-700',
  none: 'border-slate-200 bg-slate-50 text-slate-500',
  error: 'border-gray-200 bg-gray-100 text-gray-600',
};

const RATING_CLASSES: Record<Rating, string> = {
  A: 'bg-green-50 text-green-700 border-green-200',
  B: 'bg-teal-50 text-teal-700 border-teal-200',
  C: 'bg-amber-50 text-amber-700 border-amber-200',
  D: 'bg-orange-50 text-orange-700 border-orange-200',
  E: 'bg-red-50 text-red-700 border-red-200',
};

const TRACE_TONE_CLASSES: Record<TraceLine['tone'], string> = {
  default: 'border-veritas-navy/10 bg-white text-veritas-navy',
  error: 'border-red-200 bg-red-50 text-red-700',
  working: 'border-amber-200 bg-amber-50 text-amber-700',
};

function parseEvidenceJson(value: VerificationResponse['evidenceJson']) {
  if (!value) return null;
  return typeof value === 'string' ? JSON.parse(value) as EvidenceJson : value;
}

function formatTimestamp(value: string) {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatToolName(tool: string) {
  if (tool === 'realityDefender') return 'Reality Defender';
  return tool
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function mapEventToTrace(event: StreamEvent): TraceLine[] {
  const metadata = event.metadata ?? {};
  const lines: TraceLine[] = [];

  if (event.type === 'refinement_triggered') {
    lines.push({
      id: `${event.id}-main`,
      label: `↻  ${String(metadata.description ?? 'Refinement triggered')}`,
      tone: 'working',
    });
  } else if (event.type === 'analysis_failed') {
    lines.push({
      id: `${event.id}-main`,
      label: `✗  ${String(metadata.error ?? 'Analysis failed')}`,
      tone: 'error',
    });
  } else if (EVENT_LABELS[event.type]) {
    lines.push({
      id: `${event.id}-main`,
      label: EVENT_LABELS[event.type],
      tone: event.type === 'merge_complete' ? 'working' : 'default',
    });
  }

  const erroredApis = Array.isArray(metadata.erroredApis)
    ? metadata.erroredApis.filter((item): item is string => typeof item === 'string')
    : [];

  for (const tool of erroredApis) {
    lines.push({
      id: `${event.id}-${tool}`,
      label: `✗  ${formatToolName(tool)} failed — continuing with available results`,
      tone: 'error',
    });
  }

  if (typeof metadata.error === 'string' && event.type !== 'analysis_failed' && erroredApis.length === 0) {
    const label = event.type.replace(/_complete$/, '').replace(/_/g, ' ');
    lines.push({
      id: `${event.id}-error`,
      label: `✗  ${label} failed — continuing with available results`,
      tone: 'error',
    });
  }

  return lines;
}

export default function Results() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [status, setStatus] = useState<VerificationStatus>('submitted');
  const [report, setReport] = useState<EvidenceJson | null>(null);
  const [custodyEvents, setCustodyEvents] = useState<StreamEvent[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showTrace, setShowTrace] = useState(true);
  const eventSourceRef = useRef<EventSource | null>(null);
  const pollRef = useRef<number | null>(null);

  const railwayUrl = getRailwayUrl();

  const mergeCustodyEvents = useCallback((incoming: StreamEvent[]) => {
    if (incoming.length === 0) return;

    setCustodyEvents((current) => {
      const seen = new Set(current.map((event) => event.id));
      const next = [...current];

      for (const event of incoming) {
        if (seen.has(event.id)) continue;
        seen.add(event.id);
        next.push(event);
      }

      return next;
    });
  }, []);

  const fetchVerification = useCallback(async () => {
    if (!id) {
      navigate(routes.app);
      return null;
    }

    const response = await fetch(`${railwayUrl}/verification/${id}`);
    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Verification not found.');
      }

      const text = await response.text();
      throw new Error(text.slice(0, 200) || 'Failed to load verification.');
    }

    const payload = await response.json() as VerificationResponse;
    setStatus(payload.status);
    mergeCustodyEvents(payload.custodyEvents ?? []);

    if (payload.status === 'completed') {
      setReport(parseEvidenceJson(payload.evidenceJson));
    }

    if (payload.status === 'failed') {
      const parsed = parseEvidenceJson(payload.evidenceJson);
      const message =
        parsed && 'ratingReason' in parsed
          ? parsed.ratingReason
          : typeof payload.evidenceJson === 'string'
            ? (() => {
                try {
                  return (JSON.parse(payload.evidenceJson) as { error?: string }).error ?? 'Analysis failed.';
                } catch {
                  return 'Analysis failed.';
                }
              })()
            : 'Analysis failed.';

      setError(message);
    }

    return payload;
  }, [id, mergeCustodyEvents, navigate, railwayUrl]);

  useEffect(() => {
    if (!id) {
      navigate(routes.app);
      return;
    }

    let isActive = true;

    const initialize = async () => {
      try {
        const payload = await fetchVerification();
        if (!isActive || !payload) return;

        const eventSource = new EventSource(`${railwayUrl}/stream/${id}`);
        eventSourceRef.current = eventSource;

        eventSource.onmessage = (message) => {
          const event = JSON.parse(message.data) as StreamEvent;
          mergeCustodyEvents([event]);

          if (event.type === 'analysis_complete') {
            eventSource.close();
            void fetchVerification().catch((err) => {
              setError(err instanceof Error ? err.message : 'Failed to load final report.');
            });
          }

          if (event.type === 'analysis_failed') {
            setStatus('failed');
            eventSource.close();
          }
        };

        eventSource.onerror = () => {
          if (eventSource.readyState === EventSource.CLOSED) {
            void fetchVerification().catch(() => {});
          }
        };

        if (payload.status !== 'completed' && payload.status !== 'failed') {
          pollRef.current = window.setInterval(() => {
            void fetchVerification().catch(() => {});
          }, 2000);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load verification.');
      }
    };

    void initialize();

    return () => {
      isActive = false;
      eventSourceRef.current?.close();
      if (pollRef.current !== null) {
        window.clearInterval(pollRef.current);
      }
    };
  }, [fetchVerification, id, mergeCustodyEvents, navigate, railwayUrl]);

  useEffect(() => {
    if (status === 'completed' || status === 'failed') {
      if (pollRef.current !== null) {
        window.clearInterval(pollRef.current);
        pollRef.current = null;
      }
    }
  }, [status]);

  const printTimestamp = useMemo(() => {
    if (!report) return '';
    return formatTimestamp(report.fileInfo.analysisTimestamp);
  }, [report]);

  const trace = useMemo(
    () => custodyEvents.flatMap((event) => mapEventToTrace(event)),
    [custodyEvents],
  );

  const filePreview = useMemo(() => {
    const stored = localStorage.getItem('checkio_file');
    if (!stored) return null;

    try {
      const parsed = JSON.parse(stored) as StoredFileInfo;
      return parsed.preview ?? null;
    } catch {
      return null;
    }
  }, []);

  const fileHash = report?.fileInfo.fileHash ?? localStorage.getItem('checkio_hash') ?? '';

  return (
    <div className="min-h-screen bg-veritas-gray-50 font-sans text-veritas-navy">
      <div className="app-header h-12 bg-veritas-navy flex items-center justify-between px-4 sm:px-6">
        <a href={routes.landing} className="flex items-center justify-center shrink-0" aria-label="Home">
          <img src="/brand/veritas-inverse.svg" alt="Veritas" className="h-8 w-auto" />
        </a>
      </div>

      <div className="print-report-header hidden items-center justify-between border-b border-veritas-navy/10 pb-4 text-xs uppercase tracking-[0.18em] text-veritas-gray-500">
        <img src="/brand/veritas-primary.svg" alt="Veritas" className="h-6 w-auto" />
        <span className="max-w-[320px] break-all text-right normal-case tracking-normal text-veritas-navy">
          {fileHash}
        </span>
      </div>

      <div className="results-layout mx-auto flex w-full max-w-5xl flex-col gap-8 px-4 py-12">
        <div className="text-center">
          <p className="mb-2 text-xs font-semibold uppercase tracking-[0.24em] text-veritas-gray-400">
            Results
          </p>
          <h1 className="text-4xl font-light">
            {status === 'completed' ? 'Veritas forensic analysis' : 'Running live analysis'}
          </h1>
          <p className="mx-auto mt-3 max-w-2xl text-sm text-veritas-gray-500">
            {status === 'completed'
              ? 'The structured report is ready below. Use print to generate the investor-facing PDF.'
              : 'Streaming each custody event as the forensic workflow completes.'}
          </p>
        </div>

        {error && (
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="results-grid grid gap-8 lg:grid-cols-[minmax(0,1fr)_360px]">
          <div className="space-y-6">
            {report && (
              <>
                <section className="rounded-2xl border border-veritas-navy/8 bg-white p-6 shadow-veritas-soft">
                  <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_240px]">
                    <div className="space-y-4">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-veritas-gray-400">
                          Veritas forensic analysis
                        </p>
                        <h2 className="mt-2 text-2xl font-semibold break-words">{report.fileInfo.fileName}</h2>
                        <p className="mt-2 text-sm text-veritas-gray-500">{printTimestamp}</p>
                      </div>

                      {filePreview ? (
                        <img
                          src={filePreview}
                          alt={report.fileInfo.fileName}
                          className="h-64 w-full rounded-2xl border border-veritas-navy/10 object-cover"
                        />
                      ) : (
                        <div className="flex h-64 w-full items-center justify-center rounded-2xl border border-dashed border-veritas-navy/10 bg-veritas-gray-50 text-sm text-veritas-gray-400">
                          Image preview unavailable
                        </div>
                      )}
                    </div>

                    <div className={`flex h-fit items-center gap-4 rounded-2xl border px-5 py-5 ${RATING_CLASSES[report.rating]}`}>
                      <span className="text-5xl font-semibold">{report.rating}</span>
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.18em]">Rating</p>
                        <p className="text-sm font-medium">{report.ratingLabel}</p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 rounded-2xl border border-veritas-navy/8 bg-veritas-gray-50 px-5 py-4">
                    <p className="text-base text-veritas-navy">{report.ratingReason}</p>
                  </div>
                </section>

                <section className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold">Analysis results</h3>
                    <div className="results-actions flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => setShowTrace((current) => !current)}
                        className="rounded-xl border border-veritas-navy/10 bg-white px-4 py-2 text-sm font-medium text-veritas-navy transition hover:border-veritas-navy/30"
                      >
                        {showTrace ? 'Hide trace' : 'Show trace'}
                      </button>
                      <button
                        type="button"
                        onClick={() => window.print()}
                        className="print-button rounded-xl bg-veritas-navy px-4 py-2 text-sm font-semibold text-white transition hover:bg-veritas-navy/90"
                      >
                        Download as PDF
                      </button>
                    </div>
                  </div>

                  <div className="grid gap-4">
                    {report.evidence.map((item) => (
                      <article key={`${item.tool}-${item.label}`} className="evidence-card rounded-2xl border border-veritas-navy/8 bg-white p-5 shadow-veritas-soft">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                          <div>
                            <h4 className="text-lg font-semibold">{item.label}</h4>
                            <p className="mt-2 text-sm leading-6 text-veritas-gray-500">{item.detail}</p>
                          </div>
                          <span className={`inline-flex w-fit rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] ${SIGNAL_CLASSES[item.signal]}`}>
                            Signal: {item.signal}
                          </span>
                        </div>

                        {item.sections && item.sections.length > 0 && (
                          <div className="mt-4 space-y-2 border-t border-veritas-navy/8 pt-4">
                            {item.sections.map((section) => (
                              <div key={`${item.tool}-${section.label}`} className="grid gap-1 sm:grid-cols-[120px_minmax(0,1fr)]">
                                <p className="text-sm font-medium text-veritas-gray-500">{section.label}</p>
                                <p className="text-sm text-veritas-navy">{section.value}</p>
                              </div>
                            ))}
                          </div>
                        )}

                        {item.imageUrl && (
                          <img
                            src={item.imageUrl}
                            alt={`${item.label} heatmap`}
                            className="mt-4 w-full rounded-2xl border border-veritas-navy/10"
                          />
                        )}
                      </article>
                    ))}
                  </div>
                </section>

                <section className="chain-of-custody rounded-2xl border border-veritas-navy/8 bg-white p-6 shadow-veritas-soft">
                  <h3 className="text-lg font-semibold">Chain of custody</h3>
                  <div className="mt-4 space-y-3 text-sm">
                    <div className="grid gap-1 sm:grid-cols-[140px_minmax(0,1fr)]">
                      <p className="text-veritas-gray-500">File hash</p>
                      <p className="break-all text-veritas-navy">{report.fileInfo.fileHash}</p>
                    </div>
                    <div className="grid gap-1 sm:grid-cols-[140px_minmax(0,1fr)]">
                      <p className="text-veritas-gray-500">Analysis timestamp</p>
                      <p>{formatTimestamp(report.fileInfo.analysisTimestamp)}</p>
                    </div>
                    <div className="grid gap-1 sm:grid-cols-[140px_minmax(0,1fr)]">
                      <p className="text-veritas-gray-500">RFC 3161 note</p>
                      <p>
                        {report.fileInfo.rfc3161TokenUrl
                          ? 'Timestamp token stored with the verification record.'
                          : 'Timestamp attempt unavailable for this run.'}
                      </p>
                    </div>
                  </div>
                </section>
              </>
            )}
          </div>

          {(status !== 'completed' || showTrace) && (
            <aside className="trace-panel rounded-2xl border border-veritas-navy/8 bg-white p-6 shadow-veritas-soft">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-veritas-gray-400">
                    Live trace
                  </p>
                  <p className="mt-1 text-lg font-semibold">Custody events</p>
                </div>
                {status !== 'completed' && status !== 'failed' && (
                  <span className="flex h-9 w-9 items-center justify-center rounded-full border border-veritas-navy/10 bg-veritas-gray-50">
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-veritas-navy border-t-transparent" />
                  </span>
                )}
              </div>

              <div className="mt-6 space-y-3">
                {trace.length === 0 ? (
                  <div className="rounded-xl border border-dashed border-veritas-navy/15 bg-veritas-gray-50 px-4 py-5 text-sm text-veritas-gray-500">
                    Waiting for the first custody event…
                  </div>
                ) : (
                  trace.map((line) => (
                    <div key={line.id} className={`rounded-xl border px-4 py-3 text-sm ${TRACE_TONE_CLASSES[line.tone]}`}>
                      {line.label}
                    </div>
                  ))
                )}
              </div>

              <div className="mt-6 border-t border-veritas-navy/8 pt-4 text-xs text-veritas-gray-400">
                Reference: {id}
              </div>
            </aside>
          )}
        </div>
      </div>
    </div>
  );
}
